import { getIn, connect as withFormik } from 'formik'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import { isValidElement } from 'react'
import { FormattedMessage } from 'react-intl'

import defaultProps from '@/components/Form/defaultProps'
import propTypes, { formik } from '@/components/Form/propTypes'
import { Info } from '@/components/Icons'
import TooltipTrigger from '@/components/Tooltip/TooltipTrigger'
import cn from '@/utilities/cn'

import messages from './messages'

import './index.scss'

const Field = ({
  children,
  className,
  description,
  descriptionPosition = 'top',
  disabled,
  formik,
  id,
  label,
  labelLocation,
  name,
  onChange,
  render,
  required,
  tooltip,
  showErrors,
  oversizedTooltip,
  value: defaultValue,
  ...props
}: any) => {
  const error = get(formik.errors, name)
  const touched = getIn(formik.touched, name)
  const value = defaultValue || getIn(formik.values, name)

  if (onChange) {
    const handleChange = formik.handleChange

    formik.handleChange = (e: any) => {
      onChange(e)
      handleChange(e)
    }
  }

  // do NOT pass `required` to `input` as our validation messages should be used over built-in browser validation
  return (
    <div
      className={cn(
        'form__field',
        className,
        { 'form__field--disabled': disabled },
        { 'form__field--error': touched && !!error && showErrors },
        { 'form__field--required': required }
      )}
    >
      {(!labelLocation || labelLocation !== 'after') && !!label && (
        <label className={descriptionPosition === 'inline' ? '' : 'field__label'} htmlFor={id || name}>
          {!!required && (
            <span className="label__icon">
              <FormattedMessage {...messages.requiredIcon} />
            </span>
          )}

          {!!label &&
            (descriptionPosition === 'inline' ? <span className="field__label inline-block">{label}</span> : label)}
          {descriptionPosition === 'inline' && !!description && (
            <span className="field__description pl-2.5">{description}</span>
          )}

          {!!tooltip && (
            <TooltipTrigger overlay={tooltip} oversized={oversizedTooltip}>
              <span className="label__tooltip">
                <Info />
              </span>
            </TooltipTrigger>
          )}
        </label>
      )}

      {descriptionPosition === 'top' && !!description && <span className="field__description">{description}</span>}

      {render
        ? render({ ...props, className: 'field__input', disabled, formik, id: id || name, name, value })
        : children}

      {!!labelLocation && labelLocation === 'after' && !!label && (
        <label className="field__label" htmlFor={id || name}>
          {!!required && (
            <span className="label__icon">
              <FormattedMessage {...messages.requiredIcon} />
            </span>
          )}

          {!!label && label}

          {!!tooltip && (
            <TooltipTrigger overlay={tooltip} oversized={oversizedTooltip}>
              <span className="label__tooltip">
                <Info />
              </span>
            </TooltipTrigger>
          )}
        </label>
      )}

      {!!(touched && error && showErrors) && (typeof error === 'string' || isValidElement(error)) && (
        <span className="field__error">{error}</span>
      )}

      {descriptionPosition === 'bottom' && !!description && (
        <span className="field__description_bottom">{description}</span>
      )}
    </div>
  )
}

export { /** @deprecated */ Field }

Field.displayName = 'Field'

Field.propTypes = {
  children: PropTypes.node,
  className: propTypes.className,
  description: propTypes.description,
  descriptionPosition: propTypes.description,
  disabled: propTypes.disabled,
  formik: formik.isRequired,
  id: propTypes.id,
  label: propTypes.label,
  labelLocation: propTypes.labelLocation,
  onChange: propTypes.onChange,
  render: PropTypes.func,
  required: propTypes.required,
  tooltip: propTypes.tooltip,
  showErrors: PropTypes.bool,
  oversizedTooltip: PropTypes.bool
}

Field.defaultProps = {
  children: null,
  className: defaultProps.className,
  description: defaultProps.description,
  disabled: defaultProps.disabled,
  id: defaultProps.id,
  label: defaultProps.label,
  labelLocation: defaultProps.labelLocation,
  onChange: defaultProps.onChange,
  render: null,
  required: defaultProps.required,
  tooltip: defaultProps.tooltip,
  showErrors: true,
  oversizedTooltip: false
}

export default withFormik(Field)
